import {
  defineQueries,
  type ExpressionFactory,
  type Query,
} from "@rocicorp/zero";

export const WORKFLOW_RUNS_LIMIT = 250;

import * as z from "zod";
import {
  FEDERATED_API_KEY_NAME_PREFIX,
  USER_API_KEY_NAME_PREFIX,
} from "./apiKeyPrefix";
import {
  type Context,
  principalMatch,
  canReadApiKey,
  canReadAuditLog,
  canReadAuditLogDestination,
  canReadBranch,
  canReadConnector,
  canReadDeletedWorkflow,
  canReviewGrantRequest,
  canReadDuplicateWorkflowPair,
  canReadDuplicateWorkflowReview,
  canReadEgressRule,
  canReadNetwork,
  canReadOidcProvider,
  canReadRouteFavorite,
  canReadRouteView,
  canReadRun,
  canReadServiceAccount,
  canReadSkill,
  canReadSpace,
  canReadSpaceAsMemberOrDiscover,
  canReadSsoProvider,
  canReadWorkflow,
  canReadWorkflowFavorite,
  canReadWorkflowView,
  canReadPersonalChat,
  canReadPersonalChats,
  isHumanManagedServiceAccountKey,
  tenantGrantExists,
} from "./authorization";
import {
  type AuditLog,
  type BranchContributor,
  type ChatEntry,
  type CommitRoute,
  type RoleGrant,
  type Schema,
  type Space,
  type Workflow,
  type WorkflowBranch,
  type WorkflowCommit,
  zql,
} from "./generatedDrizzleSchema";
import { escapeLikePattern } from "./likePattern";
import {
  commitStepUsages,
  liveConnectorUsageBySpaceQuery,
  liveConnectorUsageQuery,
} from "./liveConnectorUsage";
import { ALLOW_ALL_ROLE_IDS } from "./rbac/roles";
import { defineTenantQuery } from "./defineTenantQuery";

// Shared relation builders for workflow data. The Query<Table, Schema, Row>
// types give us proper inference through each chain, so the extracted callbacks
// preserve full type safety at every call site.

const fullChatEntries = (e: Query<"chatEntries", Schema, ChatEntry>) =>
  e
    .orderBy("createdAt", "asc")
    .related("userMessage", (um) =>
      um.related("user").related("serviceAccount")
    )
    .related("assistantMessage")
    .related("toolCall")
    .related("assistantReasoning")
    .related("systemMessage")
    .related("userAction", (ua) => ua.related("user"))
    .related("compaction")
    .related("attachments");

const withPersonalSpaceOwner = (
  s: Query<"spaces", Schema, Space | undefined>
) => s.related("roleGrantsAsScope", (rg) => rg.related("user").limit(1));

const recentContributors =
  (since: number) =>
  (c: Query<"branchContributors", Schema, BranchContributor>) =>
    c
      .where("lastMessagedAt", ">=", since)
      .related("user")
      .orderBy("lastMessagedAt", "desc")
      .limit(25);

export function roundedHoursAgo(hours: number): number {
  const oneHour = 60 * 60 * 1000;
  return Math.floor((Date.now() - hours * oneHour) / oneHour) * oneHour;
}

// Day-grained windows for the heavy monitoring queries: every distinct `since`
// is a brand-new query the view-syncer must hydrate from scratch, so rounding
// to the day instead of the hour makes that 24× rarer.
export function roundedDaysAgo(days: number): number {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.floor((Date.now() - days * oneDay) / oneDay) * oneDay;
}

// A duplicate-pair side with everything its review card renders: space badge,
// live-branch badge state, and connector avatars.
const monitorPairWorkflow = (
  w: Query<"workflows", Schema, Workflow | undefined>
) =>
  w.related("space", withPersonalSpaceOwner).related("branches", (b) =>
    b
      .where("name", "main")
      .where("headCommitId", "IS NOT", null)
      .related(
        "headCommit",
        (c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>) =>
          c
            .related("appUsages", (u) =>
              commitStepUsages(u).related("connectedApp", (a) =>
                a.related("oauthConnection")
              )
            )
            .one()
      )
      .one()
  );

const listRowBranch =
  (activitySince: number) =>
  (b: Query<"workflowBranches", Schema, WorkflowBranch>) =>
    b
      .related("user")
      .related("contributors", recentContributors(activitySince))
      .related("headCommitChat")
      .related("headCommit", (c) =>
        c.related("appUsages", commitStepUsages).one()
      );

const featured = (r: Query<"commitRoutes", Schema, CommitRoute>) =>
  r.where("routeType", "IS NOT", null);

const workflowBranches =
  (userId: string) => (b: Query<"workflowBranches", Schema, WorkflowBranch>) =>
    b
      .where(({ or, cmp }) => or(cmp("name", "main"), cmp("userId", userId)))
      .related("user")
      .related("headCommitChat")
      .related("headCommit", (c) =>
        c
          .related("appUsages", commitStepUsages)
          .related("routes", featured)
          .related("schedules")
          .related("links")
          .one()
      )
      .orderBy("createdAt", "asc");

// A separate copy rather than a shared/parameterized helper: both a conditional
// `.related("files", …)` and factoring the head-commit callback into a typed
// variable drop the file relation from Zero's inferred row type.
const workflowBranchesWithReadme =
  (userId: string) => (b: Query<"workflowBranches", Schema, WorkflowBranch>) =>
    b
      .where(({ or, cmp }) => or(cmp("name", "main"), cmp("userId", userId)))
      .related("user")
      .related("headCommitChat")
      .related("headCommit", (c) =>
        c
          .related("files", (f) => f.where("path", "README.md").related("blob"))
          .related("appUsages", commitStepUsages)
          .related("routes", featured)
          .related("schedules")
          .related("links")
          .one()
      )
      .orderBy("createdAt", "asc");

const workflowBranchesForLinks =
  (userId: string, branchId?: string) =>
  (b: Query<"workflowBranches", Schema, WorkflowBranch>) => {
    const base = branchId ? b.where("id", branchId) : b;
    return base
      .where(({ or, cmp }) => or(cmp("name", "main"), cmp("userId", userId)))
      .related("user")
      .related("headCommitChat")
      .related("headCommit", (c) =>
        c
          .related("appUsages", commitStepUsages)
          .related("routes", featured)
          .related("schedules")
          .related("links")
          .one()
      )
      .orderBy("createdAt", "asc");
  };

const workflowBranchSummaries = (
  b: Query<"workflowBranches", Schema, WorkflowBranch>
) =>
  b
    .related("user")
    .related("contributors", (c) => c.related("user"))
    .related("commits", (c) => c.where("message", "IS NOT", null))
    .related("headCommitChat")
    .related("spaceBranchMembers", (m) => m.where("deletedAt", "IS NOT", null))
    .orderBy("createdAt", "asc");

const roleGrantsWithMembers = (g: Query<"roleGrants", Schema, RoleGrant>) =>
  g
    .related("customRole", (cr) => cr.related("rolePermissions"))
    .related("userGroup", (ug) =>
      ug.related("roleGrantsAsScope", (mg) => mg.related("user")).one()
    )
    .related("user");

// Grant consumers use principal IDs and load principal details separately.
const leanScopeGrants = (g: Query<"roleGrants", Schema, RoleGrant>) =>
  g.related("customRole", (cr) => cr.related("rolePermissions"));

const connectorDetails = (connectedAppId: string, ctx: Context) =>
  zql.connectedApps
    .where("id", connectedAppId)
    .where(canReadConnector(ctx))
    .related("oauthConnection")
    .related("roleGrantsAsScope", leanScopeGrants)
    .one();

const connectorIndex = (ctx: Context) =>
  zql.connectedApps
    .where("fresh", false)
    .where(canReadConnector(ctx))
    .orderBy("name", "asc")
    .related("oauthConnection")
    .related("createdBy")
    .related("roleGrantsAsScope", leanScopeGrants);

export const queries = defineQueries({
  allUsers: defineTenantQuery(() => zql.user.orderBy("createdAt", "asc")),

  spaceMembers: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      roleGrantsWithMembers(
        zql.roleGrants
          .where("scopeId", args.spaceId)
          .whereExists("scopeAsSpace", (s) => s.where(canReadSpace(ctx)))
      )
  ),

  userBranchPresence: defineTenantQuery(
    z.object({ since: z.number().optional() }).default({}),
    ({ args, ctx }) =>
      zql.userBranchPresence
        .where("lastSeenAt", ">", args.since ?? 0)
        .whereExists("branch", (b) => b.where(canReadBranch(ctx)), {
          scalar: true,
        })
        .related("user")
  ),

  currentUser: defineTenantQuery(({ ctx }) =>
    zql.user.where("id", ctx.userId).one()
  ),

  currentTenant: defineTenantQuery(() => zql.tenants.one()),

  tenantSealingKeys: defineTenantQuery(() =>
    zql.tenantKeys.where("retiredAt", "IS", null)
  ),

  personalSpace: defineTenantQuery(({ ctx }) =>
    zql.spaces
      .where("mode", "personal")
      .whereExists("roleGrantsAsScope", (rg) => rg.where("userId", ctx.userId))
      .one()
  ),

  personalSpaceOwners: defineTenantQuery(({ ctx }) =>
    zql.spaces
      .where("mode", "personal")
      .where(canReadSpace(ctx))
      .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
  ),

  spaces: defineTenantQuery(
    z.object({
      settingsView: z.boolean(),
    }),
    ({ ctx, args: { settingsView } }) =>
      zql.spaces
        .where(
          settingsView ? canReadSpace(ctx) : canReadSpaceAsMemberOrDiscover(ctx)
        )
        .related("roleGrantsAsScope", leanScopeGrants)
        .orderBy("name", "asc")
  ),

  // Only relation is the current user's own grants on each space, so the UI
  // can tell member from discoverable-joiner without a second query.
  spacesLite: defineTenantQuery(
    z.object({ includeAdminSpaces: z.boolean().optional() }),
    ({ ctx, args: { includeAdminSpaces } }) =>
      zql.spaces
        .where(
          includeAdminSpaces
            ? canReadSpace(ctx)
            : canReadSpaceAsMemberOrDiscover(ctx)
        )
        .related("roleGrantsAsScope", (rg) =>
          rg.where(principalMatch(ctx.userId))
        )
        .orderBy("name", "asc")
  ),

  // Spaces with the grant detail needed to render the Settings access-map
  // visualization. Availability only — no user or other display relations. When
  // `spaceIds` is given the result is scoped to those spaces; otherwise it
  // covers every readable space. `canReadSpace` stays the authorization guard
  // regardless of the ids passed.
  spaceAvailabilityGrants: defineTenantQuery(
    z.object({ spaceIds: z.array(z.string()).optional() }).default({}),
    ({ ctx, args: { spaceIds } }) =>
      (spaceIds === undefined
        ? zql.spaces.where(canReadSpace(ctx))
        : zql.spaces.where((eb) =>
            eb.and(canReadSpace(ctx)(eb), eb.cmp("id", "IN", spaceIds))
          )
      ).related("roleGrantsAsScope", (rg) =>
        rg.related("customRole", (cr) => cr.related("rolePermissions"))
      )
  ),

  // Avoid relations to keep snappy
  skillsLite: defineTenantQuery(({ ctx }) =>
    zql.skills.where(canReadSkill(ctx)).orderBy("name", "asc")
  ),

  // Avoid most relations
  connectedAppsLite: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("createdBy")
  ),

  monitorAiConnectors: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
  ),

  userPreferences: defineTenantQuery(({ ctx }) =>
    zql.userPreferences.where("userId", ctx.userId).one()
  ),

  tenantCustomDomains: defineTenantQuery(() =>
    zql.tenantCustomDomains.orderBy("createdAt", "asc")
  ),

  connectorsIndex: defineTenantQuery(({ ctx }) => connectorIndex(ctx)),

  // connectorsIndex plus each connector's usage rows. The usages fetch scans
  // the connector's whole usage set per hydration (the type filter isn't
  // pushed into SQL), so only surfaces that render usage should pay for it.
  connectorsWithUsages: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("createdBy")
      .related("roleGrantsAsScope", leanScopeGrants)
      .related("usages", (u) =>
        u
          .where("type", "!=", "commit_step")
          .where("type", "!=", "workflow_slug")
      )
  ),

  // Pending share requests visible to the current user — their own, plus every
  // request they can review. Powers the pinned group on the connectors list.
  pendingGrantRequests: defineTenantQuery(({ ctx }) =>
    zql.grantRequests
      .where(canReviewGrantRequest(ctx))
      .orderBy("createdAt", "desc")
      .related("requestedByUser")
      .related("requestedGrants")
      .related("scopeAsConnector", (c) => c.related("oauthConnection"))
  ),

  // The pending request (if any) for one scope — the connector modal's banner
  // and the requester's pending state.
  grantRequestForScope: defineTenantQuery(
    z.object({ scopeId: z.string() }),
    ({ args, ctx }) =>
      zql.grantRequests
        .where("scopeId", args.scopeId)
        .where(canReviewGrantRequest(ctx))
        .orderBy("createdAt", "desc")
        .related("requestedByUser")
        .related("requestedGrants")
        .one()
  ),

  skillsIndex: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where("fresh", false)
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("gitInstallation", (installation) =>
        installation.related("source")
      )
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  skillsManagementIndex: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("gitInstallation", (installation) =>
        installation.related("source")
      )
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  networksIndex: defineTenantQuery(({ ctx }) =>
    zql.networks
      .where("disabledAt", "IS", null)
      .where(canReadNetwork(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  // Lean projections for the access map: the same rows as the *Index queries but
  // only the grant detail the graph reads, dropping the list-page relations
  // (usages, createdBy, principal grants) it never touches. oauthConnection
  // stays because ConnectorLogo's prop type requires it.
  accessGraphConnectedApps: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("roleGrantsAsScope", leanScopeGrants)
      // Usages resolve which connectors a space uses, for the drag-to-grant
      // auto-grant prompt; commit_step usages are huge and unneeded here.
      .related("usages", (u) => u.where("type", "!=", "commit_step"))
  ),

  accessGraphSkills: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where("fresh", false)
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  accessGraphGroups: defineTenantQuery(() =>
    zql.userGroups
      // Member `user` is kept for the invite-member modal's face piles (reached
      // from the access map); only the unused principal grants are dropped.
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .orderBy("name", "asc")
  ),

  loadSkill: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("id", args.skillId)
        .where(canReadSkill(ctx))
        .related("createdBy")
        .related("gitInstallation", (installation) =>
          installation.related("source", (source) =>
            source.related("connectedApp", (app) =>
              app.where(canReadConnector(ctx))
            )
          )
        )
        .related("roleGrantsAsScope", leanScopeGrants)
        .one()
  ),

  loadSkillFileIndex: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skillFiles
        .where("skillId", args.skillId)
        .whereExists("skill", (skill) => skill.where(canReadSkill(ctx)))
        .orderBy("path", "asc")
  ),

  loadSkillFile: defineTenantQuery(
    z.object({ skillId: z.string(), path: z.string() }),
    ({ args, ctx }) =>
      zql.skillFiles
        .where("skillId", args.skillId)
        .where("path", args.path)
        .whereExists("skill", (skill) => skill.where(canReadSkill(ctx)))
        .related("blob")
        .one()
  ),

  loadSkillGitSource: defineTenantQuery(
    z.object({ sourceId: z.string() }),
    ({ args, ctx }) =>
      zql.skillGitSources
        .where("id", args.sourceId)
        .where("createdByUserId", ctx.userId)
        .related("connectedApp", (app) => app.where(canReadConnector(ctx)))
        .related("installations", (installation) =>
          installation.related("skill")
        )
        .one()
  ),

  skillGroupAccess: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("id", args.skillId)
        .where(canReadSkill(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  workflowAppSlugs: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "workflow_slug")
        .where("refId", args.workflowId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
  ),

  // Superseded by liveConnectorUsageBySpace; retained for cached clients until
  // the contract phase.
  workflowSpaceMap: defineTenantQuery(
    z.object({ workflowIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", "IN", args.workflowIds)
        .where("deletedAt", "IS", null)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
  ),

  // The client-side mirror of spaceAppEdgesZql, one row per workflow carrying
  // each branch's head-commit step usages. Usages are gated on connector
  // readability — the consumer intersects with connectors the client already
  // holds, so unreadable ones would only leak, never render.
  liveConnectorUsageBySpace: defineTenantQuery(
    z.object({ spaceIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      liveConnectorUsageBySpaceQuery(args.spaceIds, (u) =>
        commitStepUsages(u).whereExists("connectedApp", (a) =>
          a.where(canReadConnector(ctx))
        )
      ).whereExists("space", (s) => s.where(canReadSpace(ctx)))
  ),

  allWorkflows: defineTenantQuery(({ ctx }) =>
    zql.workflows.where(canReadWorkflow(ctx))
  ),

  workflowsByCodeSearch: defineTenantQuery(
    z.object({ searchText: z.string() }),
    ({ args, ctx }) => {
      const searchText = args.searchText.trim();
      if (!searchText) return zql.workflows.where("id", "");

      const pattern = `%${escapeLikePattern(searchText)}%`;
      return zql.workflows
        .where(canReadWorkflow(ctx))
        .whereExists("branches", (b) =>
          b
            .where(({ or, cmp }) =>
              or(cmp("name", "main"), cmp("userId", ctx.userId))
            )
            .whereExists("headCommit", (c) =>
              c.whereExists("files", (f) =>
                f.whereExists("blob", (blob) =>
                  blob.where("content", "ILIKE", pattern)
                )
              )
            )
        );
    }
  ),

  spaceDeletionPreviewWorkflows: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("spaceId", args.spaceId)
        .where(canReadWorkflow(ctx))
        .orderBy("createdAt", "desc")
  ),

  // Workflows with an open Autofix/Autotune branch awaiting review, behind the
  // monitoring Autofix and Autotune card. The AI monitors rename the branch
  // shortly after creating it, so we can't filter branches by name; the card
  // matches each workflow's autofixBranchId/autoImproveBranchId pointer to its
  // branch. A push or reject clears the pointer, which drops the row.
  monitorAutofixAutotune: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("deletedAt", "IS", null)
      .where(({ or, cmp }) =>
        or(
          cmp("autofixBranchId", "IS NOT", null),
          cmp("autoImproveBranchId", "IS NOT", null)
        )
      )
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) => b.related("headCommitChat"))
  ),

  // Superseded by /monitoring/workflows, which collapses the rollups, run
  // outcomes, routes, and dependencies in Postgres instead of syncing them;
  // kept for cached clients until the contract phase.
  monitorSpaceWorkflowActivity: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.spaces.where(canReadSpace(ctx)).related("workflows", (w) =>
        w
          .where("deletedAt", "IS", null)
          .related("runDailyStats", (s) => s.where("day", ">=", args.since))
          .related("branches", (b) =>
            b
              .where("name", "main")
              .related("workflowRuns", (r) =>
                r
                  .where("ephemeral", false)
                  .orderBy("createdAt", "desc")
                  .limit(14)
              )
              .related(
                "headCommit",
                (
                  c: Query<
                    "workflowCommits",
                    Schema,
                    WorkflowCommit | undefined
                  >
                ) => c.related("routes").related("dependencies").one()
              )
              .one()
          )
      )
  ),

  monitorDuplicatePairs: defineTenantQuery(({ ctx }) =>
    zql.duplicateWorkflowPairs
      .where("verdict", "duplicate")
      .where(canReadDuplicateWorkflowPair(ctx))
      .orderBy("judgedAt", "desc")
      .related("workflowA", monitorPairWorkflow)
      .related("workflowB", monitorPairWorkflow)
  ),

  monitorDuplicateReviews: defineTenantQuery(({ ctx }) =>
    zql.duplicateWorkflowReviews.where(canReadDuplicateWorkflowReview(ctx))
  ),

  monitorDuplicateDetectionRun: defineTenantQuery(() =>
    zql.duplicateDetectionRuns.one()
  ),

  // Nesting stats under workflows runs the read-permission probe once per
  // workflow instead of once per (workflow, day) row.
  monitorWorkflowDailyStats: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.workflows
        .where(canReadWorkflow(ctx))
        .related("runDailyStats", (s) => s.where("day", ">=", args.since))
  ),

  // Superseded by /monitoring/workflows; kept for cached clients until the
  // contract phase.
  monitoringStorageBySpace: defineTenantQuery(({ ctx }) =>
    zql.spaceStorageStats.whereExists("space", (s) =>
      s.where(canReadSpace(ctx))
    )
  ),

  // Superseded by the units the /monitoring/workflows report carries; kept for
  // cached clients until the contract phase.
  monitoringExecutionStatsBySpace: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.spaces.where(canReadSpace(ctx)).related("executionStats", (stats) =>
        stats
          .where("bucketAt", ">=", args.since)
          .where("source", "IS NOT", null)
          .where(({ or, cmp }) =>
            or(
              cmp("source", "!=", "workflow"),
              cmp("workflowId", "IS NOT", null)
            )
          )
          .orderBy("bucketAt", "asc")
      )
  ),

  // Superseded by the units the /monitoring/workflows report carries; kept for
  // cached clients until the contract phase.
  monitoringTenantNetworkUsage: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.networkUsageBuckets
        .where("bucketAt", ">=", args.since)
        .where(
          tenantGrantExists(
            ctx,
            "tenant:spaces_read"
          ) as unknown as ExpressionFactory<"networkUsageBuckets", Schema>
        )
        .orderBy("bucketAt", "asc")
  ),

  connectorUsageDimensions: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectorUsageDimensions
        .where("connectorId", args.connectedAppId)
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
  ),

  connectedAppForWorkflow: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("backingWorkflowId", args.workflowId)
        .where("fresh", false)
        .where(canReadConnector(ctx))
        .orderBy("createdAt", "desc")
        .one()
  ),

  // Retained for cached clients, but kept details-only so they cannot recreate
  // the historical usage graph.
  loadConnectedApp: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) => connectorDetails(args.connectedAppId, ctx)
  ),

  loadConnectorDetails: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) => connectorDetails(args.connectedAppId, ctx)
  ),

  loadConnectorNonCommitUsageTotals: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("connectedAppId", args.connectedAppId)
        .where("type", "!=", "commit_step")
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
  ),

  liveConnectorWorkflowUsages: defineTenantQuery(({ ctx }) =>
    liveConnectorUsageQuery((u) =>
      commitStepUsages(u).whereExists("connectedApp", (a) =>
        a.where(canReadConnector(ctx))
      )
    ).where(canReadWorkflow(ctx))
  ),

  loadConnectorLiveUsageTotals: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("connectedAppId", args.connectedAppId)
        .where("type", "commit_step")
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
        .whereExists("branchesAtHead", (b) =>
          b.whereExists("workflow", (w) => w.where("deletedAt", "IS", null))
        )
  ),

  loadConnectionClaim: defineTenantQuery(
    z.object({ claimId: z.string() }),
    ({ args, ctx }) =>
      zql.connectionClaims
        .where("id", args.claimId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
        .one()
  ),

  // Live workflow usage, one row per branch whose head commit uses the connector.
  loadConnectedAppLiveUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowBranches
        .whereExists("headCommit", (c) =>
          c.whereExists("appUsages", (u) =>
            u
              .where("type", "commit_step")
              .where("connectedAppId", args.connectedAppId)
          )
        )
        .whereExists("workflow", (w) =>
          w
            .where("deletedAt", "IS", null)
            .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        )
        .related("headCommit", (c) =>
          c
            .related("appUsages", (u) =>
              u
                .where("type", "commit_step")
                .where("connectedAppId", args.connectedAppId)
            )
            .related("files", (f) =>
              f.where("path", "LIKE", "%/config.toml").related("blob")
            )
            .one()
        )
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("branches")
            .related("stepActivity")
            .one()
        )
  ),

  // Personal-chat usages, filtered to chats the caller can read.
  loadConnectedAppPersonalChatUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("connectedAppId", args.connectedAppId)
        .where(canReadPersonalChat(ctx))
        .related("personalChat", (pc) => pc.related("user"))
  ),

  // Count-only source for personal chats a change would disconnect: bare usage
  // rows (no owner or title) for open chats owned by the given users, so the
  // caller can size chat impact it isn't allowed to see individually.
  connectorPersonalChatUsageForUsers: defineTenantQuery(
    z.object({ connectedAppId: z.string(), userIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
        .whereExists("personalChat", (pc) =>
          pc.where("userId", "IN", args.userIds).where("closedAt", "IS", null)
        )
  ),

  // Git-sync usages, filtered to spaces the caller can read.
  loadConnectedAppGitRemoteUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "space_git_remote")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("space", withPersonalSpaceOwner)
        .related("spaceGitRemote")
  ),

  // Audit-log-destination usages, filtered to destinations the caller can read.
  loadConnectedAppAuditUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "audit_log_destination")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("auditLogDestination", (d) =>
          d.where(canReadAuditLogDestination(ctx))
        )
        .related("auditLogDestination")
  ),

  loadConnectedAppAiProviderUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args }) =>
      zql.connectedAppUsages
        .where("type", "ai_provider")
        .where("connectedAppId", args.connectedAppId)
        .related("aiProvider")
  ),

  // Git skill sources backed by this connector. A source is only ever readable
  // by its creator, so everyone else's surface as a redacted count instead.
  loadConnectedAppSkillSourceUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.skillGitSources
        .where("connectedAppId", args.connectedAppId)
        .where("createdByUserId", ctx.userId)
  ),

  // Unused: superseded by groupConnectorLiveUsageByConnector, kept for cached
  // clients until contracted away via ALLOWED_REMOVALS.
  groupConnectorLiveUsage: defineTenantQuery(
    z.object({ userGroupIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "commit_step")
        .whereExists("connectedApp", (a) =>
          a
            .where(canReadConnector(ctx))
            .whereExists("roleGrantsAsScope", (g) =>
              g.where("userGroupId", "IN", args.userGroupIds)
            )
        )
        .whereExists("commit", (c) =>
          c.whereExists("branch", (b) =>
            b.whereExists("workflow", (w) =>
              w
                .where("deletedAt", "IS", null)
                .whereExists("space", (s) => s.where(canReadSpace(ctx)))
            )
          )
        )
        .related("connectedApp", (a) =>
          a.related("roleGrantsAsScope", leanScopeGrants).one()
        )
        .related("commit", (c) =>
          c.related("branch", (b) => b.related("workflow").one()).one()
        )
  ),

  // Connectors granted to these groups, with their commit-step usages at live
  // branch heads in readable spaces. Feeds the group-removal disconnect
  // warning.
  groupConnectorLiveUsageByConnector: defineTenantQuery(
    z.object({ userGroupIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where(canReadConnector(ctx))
        .whereExists("roleGrantsAsScope", (g) =>
          g.where("userGroupId", "IN", args.userGroupIds)
        )
        .related("roleGrantsAsScope", leanScopeGrants)
        .related("usages", (u) =>
          u
            .where("type", "commit_step")
            .whereExists("branchesAtHead", (b) =>
              b.whereExists("workflow", (w) =>
                w
                  .where("deletedAt", "IS", null)
                  .whereExists("space", (s) => s.where(canReadSpace(ctx)))
              )
            )
            .related("commit", (c) =>
              c.related("branch", (b) => b.related("workflow").one()).one()
            )
        )
  ),

  // Git-sync usages of every connector granted to one of these groups,
  // restricted to readable spaces.
  groupConnectorGitRemoteUsage: defineTenantQuery(
    z.object({ userGroupIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "space_git_remote")
        .whereExists("connectedApp", (a) =>
          a
            .where(canReadConnector(ctx))
            .whereExists("roleGrantsAsScope", (g) =>
              g.where("userGroupId", "IN", args.userGroupIds)
            )
        )
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("space", withPersonalSpaceOwner)
        .related("connectedApp", (a) =>
          a.related("roleGrantsAsScope", leanScopeGrants).one()
        )
  ),

  connectedAppGroupAccess: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("id", args.connectedAppId)
        .where(canReadConnector(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  loadSpace: defineTenantQuery(
    z.object({ spaceId: z.string(), activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.spaces
        .where("id", args.spaceId)
        .where(canReadSpace(ctx))
        .related("workflows", (w) =>
          w
            .where("deletedAt", "IS", null)
            .whereExists("branches")
            .orderBy("createdAt", "desc")
            .limit(250)
            .related("createdBy")
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranchesWithReadme(ctx.userId))
        )
        .one();
    }
  ),

  spaceUserStats: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceUserStats
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("user")
  ),

  spaceVolumes: defineTenantQuery(
    z.object({ spaceId: z.string(), branchId: z.string().optional() }),
    ({ args, ctx }) => {
      const base = zql.spaceVolumes
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("branch", (b) => b.related("workflow").one())
        .orderBy("name", "asc");
      return args.branchId ? base.where("branchId", args.branchId) : base;
    }
  ),

  workflowBranchRoutes: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string().optional(),
    }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space")
        .related("branches", (b) =>
          (args.branchId
            ? b.where("id", args.branchId)
            : b.where("name", "main")
          )
            .related(
              "headCommit",
              (
                c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>
              ) =>
                c
                  .related("routes")
                  .related("files", (f) =>
                    f.where("path", "LIKE", "%/config.toml").related("blob")
                  )
                  .one()
            )
            .one()
        )
        .one()
  ),

  workflowMainRoutes: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space")
        .related("branches", (b) =>
          b
            .where("name", "main")
            .related(
              "headCommit",
              (
                c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>
              ) => c.related("routes").one()
            )
            .one()
        )
        .one()
  ),

  loadWorkflow: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space", withPersonalSpaceOwner)
        .related("branches", workflowBranchSummaries)
        .one()
  ),

  workflowPages: defineTenantQuery(
    z.object({ workflowId: z.string(), branchId: z.string().optional() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space", withPersonalSpaceOwner)
        .related("createdBy")
        .related("contributors", recentContributors(Number.MAX_SAFE_INTEGER))
        .related(
          "branches",
          workflowBranchesForLinks(ctx.userId, args.branchId)
        )
        .one()
  ),

  loadBranch: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string().optional(),
    }),
    ({ args, ctx }) => {
      const base = zql.workflowBranches
        .where("workflowId", args.workflowId)
        .where(canReadBranch(ctx))
        .related("user")
        .related("design")
        .related("contributors", (c) => c.related("user"))
        .related("commits", (c) => c.orderBy("createdAt", "asc"))
        .related("headCommitChat")
        .related("headCommit", (c) =>
          c
            .related("files", (f) => f.related("blob"))
            .related("routes")
            .one()
        );
      return (args.branchId ? base.where("id", args.branchId) : base)
        .orderBy("createdAt", "asc")
        .limit(1)
        .one();
    }
  ),

  loadBranchSummary: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowBranches
        .where("id", args.branchId)
        .where(canReadBranch(ctx))
        .one()
  ),

  personalChats: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      zql.personalChats
        .where("userId", args?.userId ?? ctx.userId)
        .where("closedAt", "IS", null)
        .where(canReadPersonalChats(ctx))
        .orderBy("createdAt", "asc")
        .related("chat", (c) => c.related("entries", fullChatEntries).one())
  ),

  // Every readable open personal chat with no relations — for per-member
  // chat counts, not for rendering chats.
  allPersonalChats: defineTenantQuery(({ ctx }) =>
    zql.personalChats
      .where("closedAt", "IS", null)
      .where(canReadPersonalChats(ctx))
  ),

  personalVolumes: defineTenantQuery(({ ctx }) =>
    zql.personalVolumes.where("userId", ctx.userId).orderBy("name", "asc")
  ),

  personalChatTabs: defineTenantQuery(({ ctx }) =>
    zql.personalChats
      .where("userId", ctx.userId)
      .where("closedAt", "IS", null)
      .related("chat")
  ),

  closedPersonalChats: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      zql.personalChats
        .where("userId", args?.userId ?? ctx.userId)
        .where("closedAt", "IS NOT", null)
        .where(canReadPersonalChats(ctx))
        .related("chat", (c) => c.related("entries"))
        .orderBy("closedAt", "desc")
  ),

  loadPersonalChat: defineTenantQuery(
    z.object({ personalChatId: z.string() }),
    ({ args, ctx }) =>
      zql.personalChats
        .where("id", args.personalChatId)
        .where(canReadPersonalChats(ctx))
        .related("chat", (c) => c.related("entries", fullChatEntries).one())
        .related("appUsages", (u) =>
          u
            .where("type", "personal_chat")
            .related("connectedApp", (ca) =>
              ca.related("oauthConnection").related("createdBy")
            )
        )
        .one()
  ),

  loadCommitChat: defineTenantQuery(
    z.object({ commitId: z.string() }),
    ({ args, ctx }) =>
      args.commitId
        ? zql.chats
            .where("commitId", args.commitId)
            .whereExists(
              "commit",
              (c) =>
                c.whereExists(
                  "branch",
                  (b) =>
                    b.whereExists(
                      "workflow",
                      (w) =>
                        w.whereExists(
                          "space",
                          (s) => s.where(canReadSpace(ctx)),
                          { scalar: true }
                        ),
                      { scalar: true }
                    ),
                  { scalar: true }
                ),
              { scalar: true }
            )
            .orderBy("createdAt", "desc")
            .related("entries", fullChatEntries)
        : zql.chats.where("commitId", "").related("entries", fullChatEntries)
  ),

  loadBranchChats: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      args.branchId
        ? zql.workflowCommits
            .where("branchId", args.branchId)
            .whereExists(
              "branch",
              (b) =>
                b.whereExists(
                  "workflow",
                  (w) =>
                    w.whereExists("space", (s) => s.where(canReadSpace(ctx)), {
                      scalar: true,
                    }),
                  { scalar: true }
                ),
              { scalar: true }
            )
            .related("chats", (c) =>
              c.related("entries", fullChatEntries).one()
            )
            .orderBy("createdAt", "asc")
        : zql.workflowCommits
            .where("id", "")
            .related("chats", (c) =>
              c.related("entries", fullChatEntries).one()
            )
  ),

  loadBranchRunsSummary: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      args.branchId
        ? zql.workflowRuns
            .where("branchId", args.branchId)
            .where(canReadRun(ctx))
            .orderBy("createdAt", "desc")
            .limit(WORKFLOW_RUNS_LIMIT)
        : zql.workflowRuns.where("id", "")
  ),

  loadRunSummary: defineTenantQuery(
    z.object({ runId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowRuns.where("id", args.runId).where(canReadRun(ctx)).one()
  ),

  loadAllCommitFiles: defineTenantQuery(
    z.object({ commitId: z.string() }),
    ({ args, ctx }) =>
      args.commitId
        ? zql.commitFiles
            .where("commitId", args.commitId)
            .whereExists(
              "commit",
              (c) =>
                c.whereExists(
                  "branch",
                  (b) =>
                    b.whereExists(
                      "workflow",
                      (w) =>
                        w.whereExists(
                          "space",
                          (s) => s.where(canReadSpace(ctx)),
                          { scalar: true }
                        ),
                      { scalar: true }
                    ),
                  { scalar: true }
                ),
              { scalar: true }
            )
            .related("blob")
        : zql.commitFiles.where("commitId", "").related("blob")
  ),

  loadStepActivity: defineTenantQuery(
    z.object({ workflowId: z.string(), branchId: z.string() }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("workflowId", args.workflowId)
        .where("branchId", args.branchId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)), {
          scalar: true,
        })
  ),

  loadStepActivityForStep: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string(),
      stepDir: z.string(),
    }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("workflowId", args.workflowId)
        .where("branchId", args.branchId)
        .where("stepDir", args.stepDir)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)), {
          scalar: true,
        })
        .one()
  ),

  loadBranchStepActivity: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("branchId", args.branchId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)), {
          scalar: true,
        })
  ),

  userRecentWorkflows: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.workflowViews
        .where("userId", ctx.userId)
        .where(canReadWorkflowView(ctx))
        .whereExists("workflow", (w) =>
          w.where("deletedAt", "IS", null).whereExists("branches")
        )
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("favorites", (f) => f.where("userId", ctx.userId).limit(1))
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranchesWithReadme(ctx.userId))
        )
        .orderBy("lastSeenAt", "desc")
        .limit(50);
    }
  ),

  userDraftBranches: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.workflowBranches
        .where("userId", ctx.userId)
        .where(canReadBranch(ctx))
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("favorites", (f) => f.where("userId", ctx.userId).limit(1))
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranchesWithReadme(ctx.userId))
        )
        .orderBy("lastChatEntryAt", "desc")
        .limit(50);
    }
  ),

  // Backed the Recents → Links tab, which no longer exists. Kept for clients
  // still running that bundle: delete after September 12 2026.
  userRecentRoutes: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.routeViews
        .where("userId", ctx.userId)
        .where(canReadRouteView(ctx))
        .whereExists("workflow", (w) =>
          w.where("deletedAt", "IS", null).whereExists("branches")
        )
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("favorites", (f) => f.where("userId", ctx.userId).limit(1))
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranches(ctx.userId))
        )
        .orderBy("lastSeenAt", "desc")
        .limit(50);
    }
  ),

  userBranches: defineTenantQuery(
    z.object({ activitySince: z.number() }),
    ({ args, ctx }) =>
      listRowBranch(args.activitySince)(zql.workflowBranches)
        .where("userId", ctx.userId)
        .where(canReadBranch(ctx))
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("branches")
        )
        .orderBy("lastChatEntryAt", "desc")
        .limit(50)
  ),

  userWorkflowViews: defineTenantQuery(({ ctx }) =>
    zql.workflowViews.where("userId", ctx.userId)
  ),

  navBranches: defineTenantQuery(({ ctx }) =>
    zql.workflowBranches
      .where("userId", ctx.userId)
      .where(canReadBranch(ctx))
      .related("workflow")
      .related("headCommitChat")
      .orderBy("lastChatEntryAt", "desc")
      .limit(50)
  ),

  navSpace: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaces
        .where("id", args.spaceId)
        .where(canReadSpace(ctx))
        .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
        .one()
  ),

  userFavoriteWorkflowIds: defineTenantQuery(({ ctx }) =>
    zql.workflowFavorites
      .where("userId", ctx.userId)
      .where(canReadWorkflowFavorite(ctx))
  ),

  userFavoriteWorkflows: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.workflowFavorites
        .where("userId", ctx.userId)
        .where(canReadWorkflowFavorite(ctx))
        .whereExists("workflow", (w) => w.whereExists("branches"))
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranchesWithReadme(ctx.userId))
        )
        .orderBy("createdAt", "desc")
        .limit(50);
    }
  ),

  userFavoriteRouteIds: defineTenantQuery(({ ctx }) =>
    zql.routeFavorites
      .where("userId", ctx.userId)
      .where(canReadRouteFavorite(ctx))
  ),

  userFavoriteRouteWorkflows: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.routeFavorites
        .where("userId", ctx.userId)
        .where(canReadRouteFavorite(ctx))
        .whereExists("workflow", (w) =>
          w.where("deletedAt", "IS", null).whereExists("branches")
        )
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranches(ctx.userId))
        )
        .orderBy("createdAt", "desc");
    }
  ),

  allWorkflowsWithLinks: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.workflows
        .where(canReadWorkflow(ctx))
        .where("deletedAt", "IS", null)
        .whereExists("branches")
        .related("space", withPersonalSpaceOwner)
        .related("createdBy")
        .related("contributors", recentContributors(activitySince))
        .related("branches", workflowBranches(ctx.userId))
        .orderBy("updatedAt", "desc")
        .limit(500);
    }
  ),

  // All users can read AI providers (api_key is a non-replicated secret column);
  // managing them requires tenant:ai_providers_manage, enforced in the mutations.
  aiConfigs: defineTenantQuery(() =>
    zql.aiProviders
      .orderBy("createdAt", "asc")
      .related("appUsage", (u) => u.related("connectedApp"))
  ),

  activeAIProvider: defineTenantQuery(() =>
    zql.aiProviders.where("active", true).one()
  ),

  monitoringAiBudgets: defineTenantQuery(() => zql.aiBudgets),

  monitoringUsageBudgets: defineTenantQuery(() => zql.connectorUsageBudgets),

  monitoringUsagePrices: defineTenantQuery(() => zql.connectorUsagePrices),

  monitorAiProviders: defineTenantQuery(() =>
    zql.aiProviders
      .orderBy("createdAt", "asc")
      .related("modelPricing")
      .related("budgets")
      .related("appUsage")
  ),

  loadAiProvider: defineTenantQuery(
    z.object({ aiProviderId: z.string() }),
    ({ args }) =>
      zql.aiProviders
        .where("id", args.aiProviderId)
        .related("appUsage", (u) => u.related("connectedApp"))
        .one()
  ),

  monitoringWorkflowValue: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("deletedAt", "IS", null)
      .whereExists("branches", (b) =>
        b.where("name", "main").where("headCommitId", "IS NOT", null)
      )
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) => b.where("name", "main"))
  ),

  branchModelPreference: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.branchModelPreferences
        .where("branchId", args.branchId)
        .where("userId", ctx.userId)
        .one()
  ),

  ssoProviders: defineTenantQuery(({ ctx }) =>
    zql.ssoProvider.where(canReadSsoProvider(ctx))
  ),

  oidcProviders: defineTenantQuery(({ ctx }) =>
    zql.oidcProviders.where(canReadOidcProvider(ctx)).orderBy("name", "asc")
  ),

  oauthApps: defineTenantQuery(() => zql.oauthClient.where("id", "IS", null)),

  oauthApp: defineTenantQuery(z.object({ id: z.string() }), ({ args }) =>
    zql.oauthClient.where("id", args.id).where("id", "IS", null).one()
  ),

  serviceAccounts: defineTenantQuery(({ ctx }) =>
    zql.serviceAccounts
      .related("federations", (f) => f.related("provider"))
      .related("apiKeys", (k) => k.where(isHumanManagedServiceAccountKey()))
      .related("roleGrantsAsPrincipal", (g) => g.related("scopeAsSpace"))
      .where(canReadServiceAccount(ctx))
      .orderBy("name", "asc")
  ),

  auditLogs: defineTenantQuery(
    z.object({
      limit: z.number(),
      searchText: z.string().optional(),
      success: z.boolean().optional(),
      sources: z.array(z.string()).optional(),
      userId: z.string().optional(),
      since: z.number().optional(),
    }),
    ({ args, ctx }) => {
      let query = zql.auditLogs.where(canReadAuditLog(ctx));
      const searchText = args.searchText?.trim();
      if (searchText) {
        query = query.where(
          "operationName",
          "ILIKE",
          `%${escapeLikePattern(searchText)}%`
        );
      }
      if (args.success !== undefined) {
        query = query.where("success", args.success);
      }
      if (args.sources && args.sources.length > 0) {
        query = query.where(
          "source",
          "IN",
          args.sources as AuditLog["source"][]
        );
      }
      if (args.userId !== undefined) {
        query =
          args.userId === "none"
            ? query.where("userId", "IS", null)
            : query.where("userId", args.userId);
      }
      if (args.since !== undefined) {
        query = query.where("timestamp", ">", args.since);
      }
      return query.orderBy("timestamp", "desc").limit(args.limit);
    }
  ),

  auditLogDestinations: defineTenantQuery(({ ctx }) =>
    zql.auditLogDestinations
      .orderBy("name", "asc")
      .related("appUsage", (u) => u.related("connectedApp"))
      .where(canReadAuditLogDestination(ctx))
  ),

  loadSpaceGitRemote: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceGitRemotes
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)), {
          scalar: true,
        })
        .related("appUsage", (u) => u.related("connectedApp"))
        .one()
  ),

  // Every PR (open, draft, merged, or closed) on the git branch this workflow
  // branch maps to, reached through its space branch, most-recently-updated
  // first so the UI can render a state badge linking the PR.
  pullRequestsForBranch: defineTenantQuery(
    z.object({ workflowBranchId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceGitPullRequests
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .whereExists("spaceBranch", (b) =>
          b.whereExists("members", (m) =>
            m.where("workflowBranchId", args.workflowBranchId)
          )
        )
        .orderBy("updatedAt", "desc")
  ),

  // The git branch this workflow branch maps to; absent until a sync has
  // reconciled the branch onto the remote.
  gitBranchForWorkflowBranch: defineTenantQuery(
    z.object({ workflowBranchId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceBranches
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .whereExists("members", (m) =>
          m.where("workflowBranchId", args.workflowBranchId)
        )
        .related("members", (m) =>
          m.where("workflowBranchId", args.workflowBranchId).one()
        )
        .one()
  ),

  featureFlags: defineTenantQuery(() =>
    zql.featureFlags
      .where("enabledAt", "IS NOT", null)
      .orderBy("createdAt", "desc")
      .related("createdBy")
  ),

  allFeatureFlags: defineTenantQuery(() =>
    zql.featureFlags.orderBy("createdAt", "desc").related("createdBy")
  ),

  networks: defineTenantQuery(({ ctx }) =>
    zql.networks
      .orderBy("createdAt", "desc")
      .related("createdBy")
      .related("egressRoutes")
      .where(canReadNetwork(ctx))
  ),

  egressRules: defineTenantQuery(({ ctx }) =>
    zql.egressRules
      .where("disabledAt", "IS", null)
      .orderBy("createdAt", "desc")
      .where(canReadEgressRule(ctx))
  ),

  loadNetwork: defineTenantQuery(
    z.object({ networkId: z.string() }),
    ({ args, ctx }) =>
      zql.networks
        .where("id", args.networkId)
        .where(canReadNetwork(ctx))
        .related("createdBy")
        .related("roleGrantsAsScope", leanScopeGrants)
        .one()
  ),

  networkGroupAccess: defineTenantQuery(
    z.object({ networkId: z.string() }),
    ({ args, ctx }) =>
      zql.networks
        .where("id", args.networkId)
        .where(canReadNetwork(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  apiKeys: defineTenantQuery(({ ctx }) =>
    zql.apikey
      .orderBy("createdAt", "desc")
      .related("space", withPersonalSpaceOwner)
      .related("user")
      .where(canReadApiKey(ctx))
      // Service account credentials (service-account keys and UI-issued
      // sessions) live on the Service accounts page; excluding them keeps this
      // list — and the settings page's visibility — scoped to the legacy
      // user/space keys being deprecated, so minting a service account
      // credential can't resurrect the page.
      .where("serviceAccountId", "IS", null)
      // Hide everything that isn't a legacy user/space key this page is for: the
      // short-lived creds auth flows mint (`device:` / `federated:` /
      // `usersession:`) and the member-modal user keys (`userkey:`,
      // createUserApiKey), so creating one of those can't resurrect the page.
      // Keep in sync if those names change.
      .where(({ and, or, cmp }) =>
        and(
          or(cmp("name", "IS", null), cmp("name", "NOT LIKE", "device:%")),
          or(
            cmp("name", "IS", null),
            cmp("name", "NOT LIKE", `${FEDERATED_API_KEY_NAME_PREFIX}%`)
          ),
          or(cmp("name", "IS", null), cmp("name", "NOT LIKE", "usersession:%")),
          or(
            cmp("name", "IS", null),
            cmp("name", "NOT LIKE", `${USER_API_KEY_NAME_PREFIX}%`)
          )
        )
      )
  ),

  // A member's member-modal API keys (name-prefixed `userkey:` by
  // createUserApiKey), for the member modal's key list. `canReadApiKey` gates it
  // to the member themselves or a tenant user-manager, so an admin can review
  // (and revoke) another member's keys.
  userApiKeys: defineTenantQuery(
    z.object({ userId: z.string() }),
    ({ ctx, args: { userId } }) =>
      zql.apikey
        .where("referenceId", userId)
        .where(canReadApiKey(ctx))
        .where("serviceAccountId", "IS", null)
        .where("name", "LIKE", `${USER_API_KEY_NAME_PREFIX}%`)
        .orderBy("createdAt", "desc")
  ),

  deletedWorkflows: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ ctx, args: { spaceId } }) =>
      zql.workflows
        .where("spaceId", spaceId)
        .where(canReadDeletedWorkflow(ctx))
        .related("branches")
        .orderBy("deletedAt", "desc")
        .limit(200)
  ),

  allUserGroups: defineTenantQuery(() =>
    zql.userGroups
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .related("roleGrantsAsPrincipal")
      .orderBy("name", "asc")
  ),

  allUserGroupsLite: defineTenantQuery(() =>
    zql.userGroups.orderBy("name", "asc")
  ),

  allUserGroupMemberships: defineTenantQuery(() =>
    zql.userGroups.related("roleGrantsAsScope").orderBy("name", "asc")
  ),

  currentUserGroups: defineTenantQuery(({ ctx }) =>
    zql.userGroups
      .whereExists("roleGrantsAsScope", (mg) => mg.where("userId", ctx.userId))
      .orderBy("name", "asc")
  ),

  allRolesLite: defineTenantQuery(() =>
    zql.roles.related("rolePermissions").orderBy("name", "asc")
  ),

  selectableUserGroups: defineTenantQuery(() =>
    zql.userGroups
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .orderBy("name", "asc")
  ),

  tenantAdminGrants: defineTenantQuery(({ ctx }) =>
    zql.roleGrants
      .where("builtinRoleId", "IN", [...ALLOW_ALL_ROLE_IDS])
      .where("scopeId", ctx.tenantId)
      .where("userId", "IS NOT", null)
      .related("user")
  ),

  roleGrantsByScope: defineTenantQuery(
    z.object({ scopeId: z.string() }),
    ({ args }) => leanScopeGrants(zql.roleGrants.where("scopeId", args.scopeId))
  ),

  currentUserGrants: defineTenantQuery(({ ctx }) =>
    zql.roleGrants
      .where(({ or, cmp, exists }) =>
        or(
          cmp("userId", ctx.userId),
          exists("userGroup", (ug) =>
            ug.whereExists("roleGrantsAsScope", (mg) =>
              mg.where("userId", ctx.userId)
            )
          )
        )
      )
      .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  currentUserDirectGrants: defineTenantQuery(({ ctx }) =>
    zql.roleGrants
      .where("userId", ctx.userId)
      .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  currentUserGroupGrants: defineTenantQuery(({ ctx }) =>
    zql.userGroups
      .whereExists("roleGrantsAsScope", (mg) => mg.where("userId", ctx.userId))
      .related("roleGrantsAsPrincipal", (g) =>
        g.related("customRole", (cr) => cr.related("rolePermissions"))
      )
  ),

  roleGrantsByUser: defineTenantQuery(
    z.object({ userId: z.string() }),
    ({ args }) =>
      zql.roleGrants.where("userId", args.userId).related("customRole")
  ),

  roleGrantsByPrincipalGroup: defineTenantQuery(
    z.object({ userGroupId: z.string() }),
    ({ args }) => zql.roleGrants.where("userGroupId", args.userGroupId)
  ),

  roleGrantsByPrincipalServiceAccount: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.roleGrants
        .where("serviceAccountId", args.serviceAccountId)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  serviceAccountKeys: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.apikey
        .where("serviceAccountId", args.serviceAccountId)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .where(isHumanManagedServiceAccountKey())
        .orderBy("createdAt", "desc")
  ),

  oauthAppAuthorizationsForServiceAccount: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args }) =>
      zql.oauthClientServiceAccount
        .where("serviceAccountId", args.serviceAccountId)
        .where("id", "IS", null)
  ),

  // The caller's own active user-acting OAuth grants (null service account):
  // third parties they connected that act as them, shown in profile so the user
  // can revoke access.
  myOAuthGrants: defineTenantQuery(({ ctx }) =>
    zql.oauthGrant
      .where("userId", ctx.userId)
      .where("serviceAccountId", "IS", null)
      .where("revokedAt", "IS", null)
      .related("client")
      .orderBy("createdAt", "desc")
  ),

  allDirectUserGrants: defineTenantQuery(() =>
    zql.roleGrants.where("userId", "IS NOT", null)
  ),

  allDirectServiceAccountGrants: defineTenantQuery(() =>
    zql.roleGrants
      .where("serviceAccountId", "IS NOT", null)
      .related("serviceAccount")
  ),
});
